// ============================================================================
// 聊天空态建议问题 pill —— 数据源判定（纯函数，无 DOM / 无网络）
//
// 两个数据源，优先级 & 语义：
//
//   1. 商户配置 widget_config.suggested_questions —— 三态，**键的存在与否本身
//      就是语义**，不能用 `length` 一把梭：
//        · 键缺失 / null / 非数组脏值 → 「没配过」→ 回落知识库标题（老应用零感知）
//        · 显式空数组 []             → 「明确关掉」→ 什么都不显示，**不发请求**
//        · 非空数组                  → 「完全接管」→ 用配置文案，**不请求知识库**
//      resolveConfiguredQuestions 用 `null` 表达第一态，用 `[]` 表达第二态——
//      调用方必须判 `=== null` 而不是判真假，否则第二态会被误当成"没配过"，
//      商户明明关掉了访客还是看得到 KB 标题。
//
//   2. 知识库文章标题（回落）—— 只在第一态走这条路，且必须先过
//      knowledge_enabled 开关（见 isKnowledgeFallbackEnabled 的注释）。
//
// 文案本地化：商户配的 text 可能是纯字符串（服务端已按 accept-language 解析过），
// 也可能是原样透传的 {lang:text} map。两种形状都要吃得下，所以这里接一个注入的
// localize（chat.js 传 i18n 的 pickLocalized：字符串原样返回、map 按当前语言取、
// 回落 en）。注入而不是直接 import，是为了让这个模块能脱离 i18n 的加载副作用被
// 单测（test/suggest-pills.test.mjs）。
// ============================================================================

// KB 回落：标题是自动派生的，长标题当按钮很难看 —— 超过 60 字符整条丢弃，最多 4 个。
export const KB_PILL_MAX = 4;
export const KB_PILL_MAX_LEN = 60;

// 商户显式配置：文案是人写的，不能因为长了就"静默吞掉商户配的东西"，所以上限
// 放宽到 160（只挡明显的脏数据/整篇文章粘贴），数量上限 6 —— 比 dash 面板允许的
// 条数**只多不少**。反过来（widget 比 dash 少）会出现"面板配了 6 条访客只看到 4 条"
// 这种无法自解释的现象。
export const CONFIGURED_PILL_MAX = 6;
export const CONFIGURED_PILL_MAX_LEN = 160;

/**
 * 归一化单条配置项 → 字符串（取不到内容返回 ''）。
 *
 * 契约形状是 `{ text: ... }`，text 可能已是字符串、也可能仍是 {lang:text} map。
 * 另外防御两种邻近写法：整条就是个字符串、以及 label/title 别名。
 * `enabled === false` 视为这条被商户单独关掉。
 */
function normalizeItem(item, localize) {
    if (typeof item === 'string') return item.trim();
    if (!item || typeof item !== 'object' || Array.isArray(item)) return '';
    if (item.enabled === false) return '';
    let raw;
    if (item.text !== undefined) raw = item.text;
    else if (item.label !== undefined) raw = item.label;
    else if (item.title !== undefined) raw = item.title;
    else return '';
    const resolved = typeof localize === 'function' ? localize(raw) : raw;
    return typeof resolved === 'string' ? resolved.trim() : '';
}

/**
 * 商户配置的建议问题 → 字符串数组，或 null（= 没配过，调用方应回落知识库）。
 *
 * @param {any} widgetConfig appConfig.widget_config（/v1/chat 下发的那份）
 * @param {(v:any)=>string} localize i18n 的 pickLocalized
 * @returns {string[]|null} null = 未配置；[] = 显式关闭；非空 = 接管
 */
export function resolveConfiguredQuestions(widgetConfig, localize) {
    if (!widgetConfig || typeof widgetConfig !== 'object') return null;
    const raw = widgetConfig.suggested_questions;
    // 非数组（含 undefined / null / 字符串脏值）一律当"没配过"——回落是最保守的
    // 选择，绝不会因为一条坏数据把老应用的既有体验清零。
    if (!Array.isArray(raw)) return null;

    const seen = new Set();
    const out = [];
    for (const item of raw) {
        const text = normalizeItem(item, localize);
        if (!text || text.length > CONFIGURED_PILL_MAX_LEN) continue;
        if (seen.has(text)) continue;
        seen.add(text);
        out.push(text);
        if (out.length >= CONFIGURED_PILL_MAX) break;
    }
    return out;
}

/**
 * 知识库回落是否放行：商户在应用设置里关掉知识库（knowledge_enabled=false）后，
 * 访客不该再看到从 KB 标题派生出来的按钮 —— 这跟 Home 首页文章区块是同一个开关
 * （home-view.js isKnowledgeEnabled），此前 pill 这条路径漏判，关了知识库按钮
 * 照旧显示。
 *
 * 注意只门控**回落**：商户显式配置的 suggested_questions 与知识库无关，不受这个
 * 开关影响（关知识库 ≠ 关建议问题，是两件事）。
 *
 * 缺字段兜底 true（存量应用 / 配置还没到达），与 home-view 的判定字面一致，
 * 另外多认一个字符串 '0'（后端历史上 0/1/'0'/'1'/bool 都出现过）。
 */
export function isKnowledgeFallbackEnabled(appConfig) {
    if (!appConfig || typeof appConfig !== 'object') return true;
    const v = appConfig.knowledge_enabled;
    return v !== false && v !== 0 && v !== '0';
}

/**
 * /v1/knowledge 列表 → 回落 pill 文案（按服务端 sort_order 顺序，取前 4 条）。
 */
export function resolveKnowledgePills(items) {
    if (!Array.isArray(items)) return [];
    const seen = new Set();
    const out = [];
    for (const article of items) {
        const title = article && typeof article.title === 'string' ? article.title.trim() : '';
        if (!title || title.length > KB_PILL_MAX_LEN) continue;
        if (seen.has(title)) continue;
        seen.add(title);
        out.push(title);
        if (out.length >= KB_PILL_MAX) break;
    }
    return out;
}
